(() => {
  if (typeof Matter === 'undefined') return;

  class FallingBits {
    constructor(container) {
      this.container = container;
      this.viewport = container.querySelector('[data-falling-bits-viewport]');
      this.itemsWrap = container.querySelector('[data-falling-bits-items]');
      this.canvasWrap = container.querySelector('[data-falling-bits-canvas]');
      this.items = [...container.querySelectorAll('[data-falling-bit]')];

      this.effectStarted = false;
      this.hasInitialized = false;
      this.raf = null;

      this.trigger = container.dataset.trigger || 'scroll';
      this.gravity = parseFloat(container.dataset.gravity || '0.8');
      this.stiffness = parseFloat(container.dataset.stiffness || '0.2');
      this.wireframes = container.dataset.wireframes === 'true';
      this.background = container.dataset.background || 'transparent';

      if (!this.viewport || !this.items.length) return;

      this.setupTrigger();
    }

    setupTrigger() {
      if (this.trigger === 'auto') {
        this.start();
        return;
      }

      if (this.trigger === 'click') {
        this.container.addEventListener('click', () => this.start(), { once: true });
        return;
      }

      if (this.trigger === 'hover') {
        this.container.addEventListener('mouseenter', () => this.start(), { once: true });
        return;
      }

      const observer = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            if (entry.isIntersecting) {
              this.start();
              observer.disconnect();
            }
          });
        },
        { threshold: 0.2 }
      );

      observer.observe(this.container);
    }

    start() {
      if (this.effectStarted || this.hasInitialized) return;
      this.effectStarted = true;
      this.hasInitialized = true;
      requestAnimationFrame(() => this.initMatter());
    }

    initMatter() {
      const { Engine, Render, Runner, World, Bodies, Mouse, MouseConstraint, Body } = Matter;

      const rect = this.viewport.getBoundingClientRect();
      const width = Math.round(rect.width);
      const height = Math.round(rect.height);

      if (!width || !height) return;

      this.engine = Engine.create();
      this.engine.world.gravity.y = this.gravity;

      this.render = Render.create({
        element: this.canvasWrap,
        engine: this.engine,
        options: {
          width,
          height,
          wireframes: this.wireframes,
          background: this.background,
          pixelRatio: window.devicePixelRatio || 1
        }
      });

      const wallOptions = {
        isStatic: true,
        render: { fillStyle: 'transparent', strokeStyle: 'transparent' }
      };

      const floor = Bodies.rectangle(width / 2, height + 30, width + 100, 60, wallOptions);
      const leftWall = Bodies.rectangle(-30, height / 2, 60, height + 100, wallOptions);
      const rightWall = Bodies.rectangle(width + 30, height / 2, 60, height + 100, wallOptions);
      const ceiling = Bodies.rectangle(width / 2, -30, width + 100, 60, wallOptions);

      this.bodies = this.items.map((item, index) => {
        const itemRect = item.getBoundingClientRect();

        const body = Bodies.rectangle(
          Math.min(width - itemRect.width / 2, Math.max(itemRect.width / 2, 60 + index * 20)),
          30 + index * 10,
          itemRect.width,
          itemRect.height,
          {
            restitution: 0.75,
            friction: 0.2,
            frictionAir: 0.015,
            render: { fillStyle: 'transparent', strokeStyle: 'transparent' }
          }
        );

        Body.setVelocity(body, { x: (Math.random() - 0.5) * 3, y: Math.random() * 1.5 });
        Body.setAngularVelocity(body, (Math.random() - 0.5) * 0.04);

        item.style.position = 'absolute';
        item.style.left = `${body.position.x}px`;
        item.style.top = `${body.position.y}px`;
        item.style.transform = 'translate(-50%, -50%)';

        return { item, body };
      });

      const dummyEl = document.createElement('div');
      const mouse = Mouse.create(dummyEl);
      const viewportRect = () => this.viewport.getBoundingClientRect();

      const onMouseMove = (e) => {
        const b = viewportRect();
        mouse.position.x = e.clientX - b.left;
        mouse.position.y = e.clientY - b.top;
        mouse.absolute.x = mouse.position.x;
        mouse.absolute.y = mouse.position.y;
      };

      const onMouseDown = (e) => {
        const b = viewportRect();
        mouse.position.x = e.clientX - b.left;
        mouse.position.y = e.clientY - b.top;
        mouse.absolute.x = mouse.position.x;
        mouse.absolute.y = mouse.position.y;
        mouse.button = e.button;
      };

      const onMouseUp = () => {
        mouse.button = -1;
      };

      const onTouchMove = () => {};
      const onTouchStart = () => {};
      const onTouchEnd = () => {};

      this.viewport.addEventListener('mousemove', onMouseMove);
      this.viewport.addEventListener('mousedown', onMouseDown);
      this.viewport.addEventListener('mouseup', onMouseUp);
      this.viewport.addEventListener('touchstart', onTouchStart, { passive: true });
      this.viewport.addEventListener('touchmove', onTouchMove, { passive: true });
      this.viewport.addEventListener('touchend', onTouchEnd, { passive: true });

      this._mouseListeners = [
        ['mousemove', onMouseMove],
        ['mousedown', onMouseDown],
        ['mouseup', onMouseUp],
        ['touchstart', onTouchStart],
        ['touchmove', onTouchMove],
        ['touchend', onTouchEnd]
      ];

      this.mouseConstraint = MouseConstraint.create(this.engine, {
        mouse,
        constraint: { stiffness: this.stiffness, render: { visible: false } }
      });

      this.render.mouse = mouse;

      World.add(this.engine.world, [
        floor,
        leftWall,
        rightWall,
        ceiling,
        this.mouseConstraint,
        ...this.bodies.map((e) => e.body)
      ]);

      this.runner = Runner.create();
      Runner.run(this.runner, this.engine);
      Render.run(this.render);

      if (this.render.canvas) {
        this.render.canvas.style.pointerEvents = 'none';
      }

      this.updatePositions = this.updatePositions.bind(this);
      this.updatePositions();

      this.handleResize = this.handleResize.bind(this);
      window.addEventListener('resize', this.handleResize, { passive: true });
    }

    updatePositions() {
      this.bodies.forEach(({ item, body }) => {
        item.style.left = `${body.position.x}px`;
        item.style.top = `${body.position.y}px`;
        item.style.transform = `translate(-50%, -50%) rotate(${body.angle}rad)`;
      });

      this.raf = requestAnimationFrame(this.updatePositions);
    }

    handleResize() {
      return;
    }

    destroy() {
      if (this.raf) cancelAnimationFrame(this.raf);

      window.removeEventListener('resize', this.handleResize);

      if (this._mouseListeners) {
        this._mouseListeners.forEach(([type, fn]) => {
          this.viewport.removeEventListener(type, fn);
        });
        this._mouseListeners = null;
      }

      if (this.render) {
        Matter.Render.stop(this.render);
        if (this.render.canvas && this.render.canvas.parentNode) {
          this.render.canvas.parentNode.removeChild(this.render.canvas);
        }
      }

      if (this.runner) Matter.Runner.stop(this.runner);

      if (this.engine) {
        Matter.World.clear(this.engine.world, false);
        Matter.Engine.clear(this.engine);
      }

      this.canvasWrap.innerHTML = '';
    }
  }

  const initFallingBits = () => {
    document.querySelectorAll('[data-falling-bits]').forEach((el) => {
      if (el.dataset.fallingBitsReady === 'true') return;
      el.dataset.fallingBitsReady = 'true';
      new FallingBits(el);
    });
  };

  document.addEventListener('DOMContentLoaded', initFallingBits);
  document.addEventListener('shopify:section:load', initFallingBits);
  document.addEventListener('shopify:block:select', initFallingBits);
})();